import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getImageAnalysis, updateImagesMetadata } from '../../services/Mercadeo/imageService';
import { ImageAnalysis, ProcessedImage, BatchUpdateResponse } from '../../types/imageMetadata';
import { putImageProductMetaData } from '../../services/Mercadeo/productoService';



const CLOUDFRONT_BASE_URL = 'https://dddaq7mtrlhh7.cloudfront.net';

const MetadataManager: React.FC = () => {
    const [analysis, setAnalysis] = useState<ImageAnalysis | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);
    const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
    const [updateResults, setUpdateResults] = useState<BatchUpdateResponse | null>(null);
    const [allImages, setAllImages] = useState<ProcessedImage[]>([]);
    const [hoveredImage, setHoveredImage] = useState<string | null>(null);
    const [showOnlyNeedingUpdate, setShowOnlyNeedingUpdate] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(50); // Límite de registros por lote
    const [updatingOracle, setUpdatingOracle] = useState<boolean>(false);
    const [appendNewImages, setAppendNewImages] = useState<boolean>(true);
    const [currentContinuationToken, setCurrentContinuationToken] = useState<string | undefined>(undefined);
    //const [paginationHistory, setPaginationHistory] = useState<string[]>([]); // Para navegación
    const [totalImages, setTotalImages] = useState<number>(0);
    const { user } = useAuth();


    //Cargar análisis de imágenes
    const loadAnalysis = async (continuationToken?: string, loadMore: boolean = false): Promise<void> => {
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            // Resetear historial cuando es nueva carga
            if (!continuationToken) {
                //setPaginationHistory([]);
            }
        }

        try {

            const result = await getImageAnalysis(continuationToken);
            console.log('Análisis cargado:', result);

            // Guardar el token actual
            if (continuationToken) {
                //setPaginationHistory(prev => [...prev, continuationToken]);
            }
            setCurrentContinuationToken(continuationToken);

            // ✅ Guardar total exacto solo si está disponible
            if (result.pagination.exactTotalCalculated && result.pagination.exactTotal) {
                setTotalImages(result.pagination.exactTotal);
            }



            if (loadMore && analysis && appendNewImages) {
                // Combinar con análisis existente
                setAnalysis(result);
                setAllImages(prevImages => [...prevImages, ...result.currentBatch.images]);
            } else {
                // Nueva carga análisis
                setAnalysis(result);
                setAllImages(result.currentBatch.images);
            }

        } catch (error) {
            console.error('Error loading analysis:', error);
            alert('Error al cargar el análisis: ' + (error as Error).message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }

    const loadMoreImages = async (): Promise<void> => {

        // if (analysis?.pagination.hasMore && analysis.pagination.nextContinuationToken) {
        //     await loadAnalysis(analysis.pagination.nextContinuationToken, true);
        // }

        if (!appendNewImages && analysis?.pagination.nextContinuationToken) {
            // Si no es modo append, hacer una nueva carga desde el token
            await loadAnalysis(analysis?.pagination?.nextContinuationToken, false);
        } else if (analysis?.pagination.hasMore && analysis.pagination.nextContinuationToken) {
            // Modo append normal
            await loadAnalysis(analysis.pagination.nextContinuationToken, true);
        }
    };



    // Manejar selección de imágenes
    const handleSelectImage = (imageKey: string, selected: boolean): void => {
        const newSelected = new Set(selectedImages);
        if (selected) {
            newSelected.add(imageKey);
        } else {
            newSelected.delete(imageKey);
        }
        setSelectedImages(newSelected);
    };

    // Seleccionar todas las imágenes que necesitan actualización
    const selectAllNeedingUpdate = (): void => {
        if (!analysis?.currentBatch.images) return;

        // Obtener imágenes válidas que necesitan actualización
        const validImagesNeedingUpdate = getValidSkuImages(allImages).filter(img => img.needsUpdate && img.metadata.needs_oracle_update);

        // Limitar a máximo 50 imágenes
        const limitedSelection = validImagesNeedingUpdate.slice(0, 50);

        if (validImagesNeedingUpdate.length > 50) {
            alert(`Se seleccionaron las primeras 50 imágenes de ${validImagesNeedingUpdate.length} disponibles.`);
        } else if (validImagesNeedingUpdate.length === 0) {
            alert('No hay imágenes válidas que necesiten actualización en este lote.');
            return;
        }

        setSelectedImages(new Set(limitedSelection.map(img => img.key)));
    };

    // Actualizar imágenes seleccionadas
    const updateSelectedImages = async (): Promise<void> => {

        const visibleImages = getVisibleImages();
        const selectedVisibleImages = visibleImages.filter(img => selectedImages.has(img.key))


        if (selectedVisibleImages.length === 0) {
            alert('No hay imágenes seleccionadas');
            return;
        }

        if (selectedVisibleImages.length > pageSize) {
            alert(`No puedes actualizar más de ${pageSize} imágenes a la vez. Selecciona un grupo más pequeño.`);
            return;
        }

        if (!window.confirm(`¿Actualizar metadata de ${selectedImages.size} imágenes?`)) {
            return;
        }

        setUpdating(true);
        try {

            const defaultMetadata = {
                usuario: 'sistema@grupociisa.com',
                ultimo_editor: 'sistema@grupociisa.com',
                uso_imagen: 'GENERAL'
            };

            const oracleResults: Record<string, any> = {};

            const principalImages = visibleImages.filter(img =>
                img.metadata?.uso_imagen === 'PRINCIPAL' ||
                (img.metadataStatus === 'complete' && img.metadata?.uso_imagen === 'PRINCIPAL')
            ).filter(img => selectedImages.has(img.key));

            /* if (!window.confirm(`¿Actualizar metadata y PRINCIPAL en Oracle de ${principalImages.length} imágenes?`)) {
                return;
            } */

            if (principalImages.length > 0) {
                // Servicio Oracle par actualizar cada imagen
                for (const image of principalImages) {
                    console.log('Actualizando imagen en Oracle:', image);

                    const oracleResponse = await putImageProductMetaData(image.skuFromFileName!, {
                        imageName: image.fileName,
                        usoImagen: 'PRINCIPAL',
                        usuario: user?.User.email || ''
                    });

                    if (oracleResponse.status === 'success') {
                        oracleResults[image.key] = {
                            success: true,
                            error: null
                        }
                    } else {
                        oracleResults[image.key] = {
                            success: false,
                            error: oracleResponse.message || 'Error desconocido'
                        }
                    }
                }
            }
            console.log('Resultados Oracle:', oracleResults);



            //Actualiza solo la metada en el S3
            const result = await updateImagesMetadata(
                selectedVisibleImages,
                defaultMetadata,
                oracleResults
            );

            console.log('Update result:', result);

            setUpdateResults(result);

            console.log(`Actualizados:`, updateResults)

            // ✅ RECARGAR EL MISMO LOTE usando el token guardado
            await loadAnalysis(currentContinuationToken, false);
            setSelectedImages(new Set());


        } catch (error) {
            console.error('Error updating images:', error);
            alert('Error al actualizar imágenes: ' + (error as Error).message);
        } finally {
            setUpdating(false);
        }
    };

    // Función helper para determinar si mostrar el botón Oracle
    const shouldShowOracleButton = (image: ProcessedImage): boolean => {
        const needsUpdate = image.metadata?.needs_oracle_update;

        // Mostrar botón si: es true, null, undefined, o no existe
        return (needsUpdate === 'true' ||
            needsUpdate === null ||
            needsUpdate === undefined ||
            needsUpdate === '') && image.metadata?.uso_imagen === 'PRINCIPAL';

    };

    // Función para determinar si Oracle está actualizado
    const isOracleUpdated = (image: ProcessedImage): boolean => {
        return image.metadata?.needs_oracle_update === 'false';
    };


    // Función para recargar el lote actual
    const reloadCurrentBatch = async (): Promise<void> => {
        await loadAnalysis(currentContinuationToken, false);
    };

    // Filtrar imágenes que necesitan actualización
    const getImagesNeedingUpdate = (): ProcessedImage[] => {
        return allImages.filter(img => img.needsUpdate) || [];
    };

    // Obtener URL de la imagen para thumbnail
    const getImageUrl = (image: ProcessedImage): string => {
        return `${CLOUDFRONT_BASE_URL}/${image.key}`;
    };

    // Formatear metadata para tooltip
    const formatMetadataForTooltip = (metadata: any): string => {
        return JSON.stringify(metadata, null, 2)
            .replace(/"/g, '')
            .replace(/{/g, '')
            .replace(/}/g, '')
            .replace(/,/g, '\n');
    };

    // Nueva función para filtrar imágenes visibles
    const getVisibleImages = (): ProcessedImage[] => {
        let filteredImages = allImages;

        // Primero filtrar por SKU válido (6 dígitos exactos)
        filteredImages = getValidSkuImages(filteredImages);

        if (showOnlyNeedingUpdate) {
            filteredImages = filteredImages.filter(img => img.needsUpdate);
        }
        return filteredImages;
    };



    // Función modificada para actualizar Oracle solo con SKUs válidos
    const updatePrincipalImagesInOracle = async (): Promise<void> => {
        const principalImages = getValidSkuImages(allImages).filter(img =>
            img.metadata?.uso_imagen === 'PRINCIPAL' ||
            (img.metadataStatus === 'complete' && img.metadata?.uso_imagen === 'PRINCIPAL')
        );

        if (principalImages.length === 0) {
            alert('No hay imágenes válidas (6 dígitos) marcadas como PRINCIPAL para actualizar en Oracle');
            return;
        }

        if (!window.confirm(`¿Actualizar ${principalImages.length} imágenes PRINCIPAL válidas en la base de datos Oracle?`)) {
            return;
        }

        setUpdatingOracle(true);
        try {
            console.log('Imágenes válidas a actualizar en Oracle:', principalImages);
            alert(`Se actualizarán ${principalImages.length} imágenes PRINCIPAL válidas en Oracle`);

            // Servicio Oracle par actualizar cada imagen
            for (const image of principalImages) {
                console.log('Actualizando imagen en Oracle:', image);
                const oracleResponse = await putImageProductMetaData(image.skuFromFileName!, {
                    imageName: image.fileName,
                    usoImagen: 'PRINCIPAL',
                    usuario: user?.User.email || ''
                });
                console.log('Respuesta de Oracle para imagen', image.fileName, ':', oracleResponse);
            }

        } catch (error) {
            console.error('Error updating Oracle:', error);
            alert('Error al actualizar imágenes en Oracle: ' + (error as Error).message);
        } finally {
            setUpdatingOracle(false);
        }
    };

    // Función para actualizar una imagen individual en Oracle
    const updateSingleImageInOracle = async (image: ProcessedImage): Promise<void> => {
        if (!window.confirm(`¿Actualizar imagen ${image.fileName} en la base de datos Oracle?`)) {
            return;
        }

        try {
            console.log('Actualizando imagen en Oracle:', image);
            // TODO: Implementar la llamada real al servicio Oracle para una imagen
            const oracleResponse = await putImageProductMetaData(image.skuFromFileName!, {
                imageName: image.fileName,
                usoImagen: 'PRINCIPAL',
                usuario: user?.User.email || ''
            });
            console.log('Respuesta de Oracle para imagen', image.fileName, ':', oracleResponse);

            alert(`Imagen ${image.fileName} actualizada en Oracle`);
        } catch (error) {
            console.error('Error updating single image in Oracle:', error);
            alert('Error al actualizar imagen en Oracle: ' + (error as Error).message);
        }
    };

    // Función para verificar si un SKU es válido (exactamente 6 dígitos)
    const isValidSkuFormat = (sku: string | null): boolean => {
        if (!sku) return false;
        return /^\d{6}$/.test(sku.toString());
    };

    // Función para obtener imágenes que cumplen con el formato 6 dígitos
    const getValidSkuImages = (images: ProcessedImage[]): ProcessedImage[] => {
        return images.filter(img => isValidSkuFormat(img.skuFromFileName));
    };


    //const imagesNeedingUpdate = getImagesNeedingUpdate();
    const validImagesNeedingUpdate = getValidSkuImages(allImages).filter(img => img.needsUpdate);
    const selectedCount = selectedImages.size;
    const hasMore = analysis?.pagination.hasMore || false;

    useEffect(() => {
        loadAnalysis();
    }, []);

    return (
        <div className="container-fluid py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="h4 mb-0">🔄 Administrador de Metadata de Imágenes</h1>
                    <p className="text-muted small mb-0">Analiza y actualiza la metadata de las imágenes en el bucket S3</p>
                </div>
                <button
                    onClick={() => loadAnalysis()}
                    disabled={loading}
                    className="btn btn-primary btn-sm"
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Actualizando...
                        </>
                    ) : (
                        '🔄 Actualizar Análisis'
                    )}
                </button>
            </div>

            {/* Estadísticas */}
            {analysis && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">📊 Estadísticas del Lote Actual </h5>
                                <small className="text-muted">
                                    Mostrando {getVisibleImages().length} de {getValidSkuImages(allImages).length} imágenes válidas •
                                    Total estimado: {analysis.pagination.estimatedTotal} imágenes
                                </small>
                            </div>
                            <div className="card-body py-2">
                                <div className="row text-center">
                                    <div className="text-muted">
                                        Mostrando {allImages.length} de {totalImages || analysis?.pagination.estimatedTotal} imágenes
                                        {totalImages > 0 && " (total exacto)"}
                                    </div>
                                    <div className="col-md-2 col-4 mb-1">
                                        <div className="text-center">
                                            <div className="h6 mb-0 text-primary">{getValidSkuImages(allImages).length}</div>
                                            <div className="text-muted">SKUs Válidos (6 dígitos)</div>
                                        </div>
                                    </div>
                                    <div className="col-md-2 col-4 mb-1">
                                        <div className="h6 mb-0 text-success">
                                            {getValidSkuImages(allImages).filter(img => img.metadataStatus === 'complete').length}
                                        </div>
                                        <div className="text-muted">Metadata Completa</div>
                                    </div>
                                    <div className="col-md-2 col-4 mb-1">
                                        <div className="text-center">
                                            <div className="h6 mb-0 text-warning">
                                                {getValidSkuImages(allImages).filter(img => img.metadataStatus === 'partial').length}
                                            </div>
                                            <div className="text-muted">Metadata Parcial</div>
                                        </div>
                                    </div>
                                    <div className="col-md-2 col-4 mb-1">
                                        <div className="text-center">
                                            <div className="h6 mb-0 text-danger">
                                                {getValidSkuImages(allImages).filter(img => img.metadataStatus === 'none').length}
                                            </div>
                                            <div className="text-muted">Sin Metadata</div>
                                        </div>
                                    </div>
                                    <div className="col-md-2 col-4 mb-1">
                                        <div className="text-center">
                                            <div className="h6 mb-0 text-danger">
                                                {getValidSkuImages(allImages).filter(img => img.needsUpdate).length}
                                            </div>
                                            <div className="text-muted">Necesitan Actualización</div>
                                        </div>
                                    </div>
                                    <div className="col-md-2 col-4 mb-1">
                                        <div className="text-center">
                                            <div className="h6 mb-0 text-info">
                                                {getValidSkuImages(allImages).filter(img =>
                                                    img.metadata?.uso_imagen === 'PRINCIPAL' ||
                                                    (img.metadataStatus === 'complete' && img.metadata?.uso_imagen === 'PRINCIPAL')
                                                ).length}
                                            </div>
                                            <div className="text-muted">Imágenes PRINCIPAL</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Información adicional sobre filtrado */}
                            <div className="row mt-2">
                                <div className="col-12">
                                    <small className="text-muted">
                                        📝 <strong>Filtro activo:</strong> Solo se consideran imágenes con SKU de exactamente 6 dígitos •
                                        {allImages.length - getValidSkuImages(allImages).length} imágenes ignoradas por formato inválido
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Nuevos Controles de Filtro y Configuración */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body py-2">
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={showOnlyNeedingUpdate}
                                            onChange={(e) => setShowOnlyNeedingUpdate(e.target.checked)}
                                            id="filterNeedingUpdate"
                                        />
                                        <label className="form-check-label small" htmlFor="filterNeedingUpdate">
                                            Mostrar solo imágenes que necesitan actualización
                                        </label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={appendNewImages}
                                            onChange={(e) => setAppendNewImages(e.target.checked)}
                                            id="appendNewImages"
                                        />
                                        <label className="form-check-label small" htmlFor="appendNewImages">
                                            Agregar nuevas imágenes a la lista actual
                                        </label>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">Límite por lote:</span>
                                        <select style={{ width: '100px' }}
                                            className="form-select"
                                            value={pageSize}
                                            onChange={(e) => setPageSize(Number(e.target.value))}
                                        >
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        onClick={updatePrincipalImagesInOracle}
                                        disabled={updatingOracle}
                                        className="btn btn-success btn-sm w-100"
                                    >
                                        {updatingOracle ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Actualizando Oracle...
                                            </>
                                        ) : (
                                            '🔄 Actualizar PRINCIPAL en Oracle'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controles de selección */}
            {analysis && validImagesNeedingUpdate.length > 0 && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card border-warning">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <small className='fw-bold'>
                                            {getVisibleImages().length} imágenes en vista •
                                            {selectedCount} seleccionadas •
                                            {validImagesNeedingUpdate.length} necesitan actualización
                                        </small>
                                        <br />
                                        <small className="text-muted">
                                            Solo se muestran imágenes con SKU de exactamente 6 dígitos
                                        </small>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button
                                            onClick={selectAllNeedingUpdate}
                                            className="btn btn-outline-secondary btn-sm position-relative"
                                            title="Selecciona máximo 50 imágenes para evitar sobrecarga"
                                        >
                                            Seleccionar Válidas
                                            {validImagesNeedingUpdate.length > 0 && (
                                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning">
                                                    {Math.min(validImagesNeedingUpdate.length, 50)}/{validImagesNeedingUpdate.length}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setSelectedImages(new Set())}
                                            className="btn btn-outline-secondary btn-sm"
                                        >
                                            Limpiar Selección
                                        </button>
                                        <button
                                            onClick={updateSelectedImages}
                                            disabled={updating || selectedCount === 0 || selectedCount > pageSize}
                                            className="btn btn-warning btn-sm"
                                            title={selectedCount > pageSize ? `Máximo ${pageSize} imágenes por lote` : ''}
                                        >
                                            {updating ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                    Actualizando...
                                                </>
                                            ) : (
                                                `🔄 Actualizar ${selectedCount} Imágenes`
                                            )}
                                        </button>
                                        <button
                                            onClick={reloadCurrentBatch}
                                            disabled={loading}
                                            className="btn btn-outline-info btn-sm"
                                            title="Recargar lote actual"
                                        >
                                            🔄
                                        </button>
                                    </div>
                                </div>
                                {selectedCount > pageSize && (
                                    <div className="text-danger small mt-1">
                                        ⚠️ Has seleccionado {selectedCount} imágenes. El límite es {pageSize}.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Resultados de actualización */}
            {updateResults && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card border-info">
                            <div className="card-header bg-info text-white py-2">
                                <h5 className="card-title mb-0">📋 Resultados de la Actualización</h5>
                            </div>
                            <div className="card-body py-2">
                                <div className="d-flex gap-4">
                                    <span className="text-success">
                                        <small><strong>✓ {updateResults.results?.successful || 0} exitosas</strong></small>
                                    </span>
                                    <span className="text-danger">
                                        <small><strong>✗ {updateResults.results?.failed || 0} fallidas</strong></small>
                                    </span>
                                </div>
                                {updateResults.message && (
                                    <small className="mt-1 mb-0 d-block">{updateResults.message}</small>
                                )}
                            </div>
                            {/* OPCIONAL: Si se  quieres mostrar detalles individuales */}
                            {/* {updateResults.results?.details && updateResults.results.details.length > 0 && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        Detalles: {updateResults.results.details.filter(r => r.success).length} exitosas,
                                        {updateResults.results.details.filter(r => !r.success).length} fallidas
                                    </small>
                                </div>
                            )}

                            {updateResults.message && (
                                <small className="mt-1 mb-0 d-block">{updateResults.message}</small>
                            )} */}
                        </div>
                    </div>
                </div>
            )}

            {/* Lista de imágenes con scroll */}
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center py-2">

                            <h6 className="card-title mb-0">
                                🖼️ Lista de Imágenes ({getVisibleImages().length})
                                {showOnlyNeedingUpdate && ` (Filtradas: ${getValidSkuImages(allImages).filter(img => img.needsUpdate).length} necesitan actualización)`}
                                <small className="text-muted d-block">
                                    Mostrando solo SKUs válidos de 6 dígitos •
                                    {getValidSkuImages(allImages).length} de {allImages.length} imágenes son válidas
                                </small>
                            </h6>
                            <div className="d-flex gap-2 align-items-center">
                                {appendNewImages && allImages.length > 0 && (
                                    <span className="badge bg-info me-2">
                                        +{allImages.length - (analysis?.currentBatch.images.length || 0)} acumuladas
                                    </span>
                                )}

                                {hasMore && (
                                    <button
                                        onClick={loadMoreImages}
                                        disabled={loadingMore}
                                        className="btn btn-outline-primary btn-sm"
                                        title={appendNewImages ? "Agregar más imágenes a la lista actual" : "Cargar siguiente lote (reemplaza lista)"}
                                    >
                                        {loadingMore ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Cargando...
                                            </>
                                        ) : appendNewImages ? (
                                            `📥 Agregar Más`
                                        ) : (
                                            `📥 Siguiente Lote`
                                        )}
                                    </button>
                                )}

                                {appendNewImages && allImages.length > (analysis?.currentBatch.images.length || 0) && (
                                    <button
                                        onClick={() => {
                                            // Resetear a solo el lote actual
                                            if (analysis) {
                                                setAllImages(analysis.currentBatch.images);
                                            }
                                        }}
                                        className="btn btn-outline-secondary btn-sm"
                                        title="Mostrar solo el lote actual"
                                    >
                                        🗑️ Limpiar Acumuladas
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {loading && !loadingMore ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <p className="mt-2 small">Cargando análisis de imágenes...</p>
                                </div>
                            ) : allImages.length === 0 ? (
                                <div className="text-center py-4 text-muted small">
                                    No hay datos de análisis disponibles
                                </div>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            <tr>
                                                <th style={{ width: '40px' }}>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={selectedCount === validImagesNeedingUpdate.length && validImagesNeedingUpdate.length > 0}
                                                            onChange={(e) => e.target.checked ? selectAllNeedingUpdate() : setSelectedImages(new Set())}
                                                        />
                                                    </div>
                                                </th>
                                                <th style={{ width: '60px' }}>Thumbnail</th>
                                                <th>Archivo</th>
                                                <th>SKU Metadata</th>
                                                <th>SKU Archivo</th>
                                                <th>Tamaño</th>
                                                <th>Estado Metadata</th>
                                                <th>Validez SKU</th>
                                                <th>Última Modificación</th>
                                                <th>Uso</th>
                                                <th style={{ width: '100px' }}>Acciones</th>
                                                <th style={{ width: '100px' }}>Estado Oracle</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getVisibleImages().map((image, index) => (
                                                <tr
                                                    key={`${image.key}-${index}`}
                                                    className={image.needsUpdate ? 'table-warning' : ''}
                                                >
                                                    {/* <td>
                                                        {index + 1}
                                                    </td> */}
                                                    {/* (image.needsUpdate && (image.metadata.needs_oracle_update || image.metadata.needsoracleupdate)) */}

                                                    <td>
                                                        {(image.needsUpdate && (image.metadata.needs_oracle_update || image.metadata.needsoracleupdate)) && (
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={selectedImages.has(image.key)}
                                                                    onChange={(e) => handleSelectImage(image.key, e.target.checked)}
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="position-relative"
                                                            onMouseEnter={() => setHoveredImage(image.key)}
                                                            onMouseLeave={() => setHoveredImage(null)}
                                                        >
                                                            <img
                                                                src={getImageUrl(image)}
                                                                alt={image.fileName}
                                                                className='img-thumbnail'
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    objectFit: 'cover',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                            />
                                                            {/* Tooltip de imagen grande */}
                                                            {hoveredImage === image.key && (
                                                                <div
                                                                    className="position-absolute start-100 ms-2 bg-white border rounded shadow-lg p-2"
                                                                    style={{
                                                                        zIndex: 1000,
                                                                        width: '300px',
                                                                        maxHeight: 'calc(100vh - 20px)', // ✅ No más alto que la pantalla 
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        overflow: 'hidden'
                                                                    }}
                                                                >
                                                                    <div style={{ height: 'min(250px, calc(100vh - 100px))' }} > {/* ✅ Altura adaptable  */}
                                                                        <img
                                                                            src={getImageUrl(image)}
                                                                            alt={image.fileName}
                                                                            className='w-100 h-100'
                                                                            style={{ objectFit: 'contain' }}
                                                                            onError={(e) => {
                                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                                                (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="text-muted text-center p-4">Imagen no disponible</div>';
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className='text-center mt-2 p-1 boder-top'>
                                                                        <small className='text-muted fw-bold' style={{ fontSize: '0.8rem' }}>
                                                                            {image.fileName}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="position-relative"
                                                            onMouseEnter={() => setHoveredImage(image.key)}
                                                            onMouseLeave={() => setHoveredImage(null)}
                                                        >
                                                            <code className="text-dark small" style={{ cursor: 'pointer' }}>
                                                                {image.fileName}
                                                            </code>
                                                        </div>
                                                        {image.error && (
                                                            <div className="text-danger small mt-1">
                                                                <strong>Error:</strong> {image.error}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <code className='small'>{image.skuFromMetadata || 'N/A'}</code>
                                                    </td>
                                                    <td>
                                                        <code className='small'>{image.skuFromFileName || 'N/A'}</code>
                                                    </td>
                                                    <td className='small'>
                                                        {(image.size / 1024).toFixed(2)} KB
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${image.metadataStatus === 'complete' ? 'bg-success' :
                                                            image.metadataStatus === 'partial' ? 'bg-warning' :
                                                                image.metadataStatus === 'error' ? 'bg-danger' : 'bg-secondary'}`}
                                                            data-bs-toggle="tooltip"
                                                            data-bs-placement="top"
                                                            title={formatMetadataForTooltip(image.metadata)}
                                                        >
                                                            {image.metadataStatus}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${image.skuValidity === 'valid' ? 'bg-success' :
                                                            image.skuValidity === 'conflict' ? 'bg-warning' :
                                                                image.skuValidity === 'error' ? 'bg-danger' : 'bg-secondary'}`}>
                                                            {image.skuValidity}
                                                        </span>
                                                    </td>
                                                    <td className='small'>
                                                        {new Date(image.lastModified).toLocaleDateString('es-ES', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td className='small'>
                                                        <span className={`badge ${image.metadata.uso_imagen === 'PRINCIPAL' ? 'bg-success' : 'bg-warning'}`}>
                                                            {image.metadata.uso_imagen || 'N/A'}
                                                        </span>

                                                    </td>

                                                   
                                                    <td>
                                                        <div className="btn-group btn-group-sm">
                                                            {/* ✅ MOSTRAR BOTÓN ORACLE SOLO CUANDO ES NECESARIO */}
                                                            {shouldShowOracleButton(image) && (
                                                                <button
                                                                    className={`btn ${image.metadata?.oraclesyncstatus === 'failed'
                                                                            ? 'btn-outline-danger'
                                                                            : image.metadata?.oraclesyncstatus === 'pending'
                                                                                ? 'btn-outline-warning'
                                                                                : 'btn-outline-success'
                                                                        }`}
                                                                    onClick={() => updateSingleImageInOracle(image)}
                                                                    title={
                                                                        image.metadata?.oraclesyncstatus === 'failed'
                                                                            ? `Error Oracle: ${image.metadata.oraclelastsyncat || 'Reintentar'}`
                                                                            : image.metadata?.oraclesyncstatus === 'pending'
                                                                                ? 'Pendiente de actualización en Oracle'
                                                                                : 'Actualizar en base de datos Oracle'
                                                                    }
                                                                    disabled={updatingOracle}
                                                                >
                                                                    {updatingOracle ? (
                                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                    ) : image.metadata?.oraclesyncstatus === 'failed' ? (
                                                                        '❌ Oracle'
                                                                    ) : image.metadata?.oraclesyncstatus === 'pending' ? (
                                                                        '⏳ Oracle'
                                                                    ) : (
                                                                        '📊 Oracle'
                                                                    )}
                                                                </button>
                                                            )}
                                                            {/* ✅ Badge cuando está actualizado */}
                                                            {isOracleUpdated(image) && (
                                                                <span className="badge bg-primary" title="Oracle actualizado">
                                                                    ✅ Oracle
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className=''>
                                                            {/* ✅ OPCIONAL: Mostrar badge cuando NO necesita actualización */}
                                                            {image.metadata?.needsoracleupdate === 'false' && (
                                                                <span className="badge bg-success" title="Oracle actualizado">
                                                                    ✅ Oracle
                                                                </span>
                                                            )}
                                                            {(image.metadata?.needsoracleupdate === 'true' ||
                                                                image.metadata?.needsoracleupdate === null ||
                                                                image.metadata?.needsoracleupdate === undefined ||
                                                                !image.metadata?.needsoracleupdate) && (
                                                                    <span className="badge bg-warning" title="Oracle necesita actualización">
                                                                        ✗ Oracle
                                                                    </span>
                                                                )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {index + 1}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/*Botón de cargar más al final */}
                                    {hasMore && (
                                        <div className="text-center p-2 border-top">
                                            <div className="d-flex justify-content-center align-items-center gap-3">
                                                <button
                                                    onClick={loadMoreImages}
                                                    disabled={loadingMore}
                                                    className="btn btn-primary"
                                                >
                                                    {loadingMore ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Cargando...
                                                        </>
                                                    ) : appendNewImages ? (
                                                        `📥 Agregar Más Imágenes (${analysis?.pagination.estimatedTotal || 0 - allImages.length} restantes)`
                                                    ) : (
                                                        `📥 Siguiente Lote (${analysis?.pagination.estimatedTotal || 0 - allImages.length} restantes)`
                                                    )}
                                                </button>

                                                {appendNewImages && allImages.length > 100 && (
                                                    <span className="text-muted small">
                                                        • {allImages.length} imágenes en lista
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Información del proceso */}
            {
                analysis && analysis.summary.totalProcessed && analysis.currentBatch.images.length > 100 && (
                    <div className="row mt-3">
                        <div className="col-12">
                            <div className="alert alert-info py-2">
                                <div className="d-flex">
                                    <div className="me-2">💡</div>
                                    <div>
                                        <strong>Proceso largo detectado:</strong> Se encontraron {analysis.currentBatch.images.length} imágenes.
                                        La actualización puede tomar varios minutos. Recomendamos seleccionar lotes pequeños para empezar.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};


export default MetadataManager;